import { useState, useEffect, useCallback } from 'react';
import { imageCache } from '../utils/imageCache';

export function useImageCache(src, preloadSources = []) {
  const [imageState, setImageState] = useState({
    isLoaded: false,
    isLoading: false,
    error: null
  });

  const loadImage = useCallback(async (imageSrc) => {
    if (!imageSrc) return;

    // Check if already cached
    if (imageCache.isCached(imageSrc)) {
      setImageState({
        isLoaded: true,
        isLoading: false,
        error: null
      });
      return;
    }

    // Start loading
    setImageState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      await imageCache.loadImage(imageSrc);
      setImageState({
        isLoaded: true,
        isLoading: false,
        error: null
      });
    } catch (error) {
      setImageState({
        isLoaded: false,
        isLoading: false,
        error: error.message
      });
    }
  }, []);

  // Load main image when src changes
  useEffect(() => {
    if (src) {
      loadImage(src);
    }
  }, [src, loadImage]);

  // Preload nearby images
  useEffect(() => {
    if (preloadSources.length > 0) {
      // Delay preloading slightly to prioritize main image
      const timeoutId = setTimeout(() => {
        imageCache.preloadImages(preloadSources);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [preloadSources]);

  return imageState;
}

export default useImageCache;
